import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, ArrowRight, Mail, MessageCircle, Instagram } from "lucide-react";
import { Reveal } from "./Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Field = ({ label, textarea, ...props }) => (
  <label className="block">
    <span className="mb-2 block text-xs uppercase tracking-luxe text-gold/70">{label}</span>
    {textarea ? (
      <textarea
        {...props}
        rows={4}
        className="w-full resize-none border-b border-gold/40 bg-transparent pb-3 text-lg font-light text-cream outline-none transition-colors placeholder:text-cream/30 focus:border-gold"
      />
    ) : (
      <input
        {...props}
        className="w-full border-b border-gold/40 bg-transparent pb-3 text-lg font-light text-cream outline-none transition-colors placeholder:text-cream/30 focus:border-gold"
      />
    )}
  </label>
);

const Channel = ({ icon: Icon, label, value, href, primary, testid }) => {
  const inner = (
    <div
      className={`group flex items-start gap-4 rounded-2xl border p-5 transition-colors ${
        primary
          ? "border-gold/40 bg-gold/5 hover:border-gold/70"
          : "border-cream/10 bg-transparent"
      }`}
      data-testid={testid}
    >
      <span
        className={`mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-full border ${
          primary ? "border-gold/50 text-gold" : "border-cream/20 text-cream/50"
        }`}
      >
        <Icon size={18} />
      </span>
      <div>
        <p className="text-xs uppercase tracking-luxe text-gold/70">{label}</p>
        <p
          className={`mt-1 font-serif text-lg ${
            primary ? "text-gold" : "text-cream/45"
          }`}
        >
          {value}
        </p>
        {primary && (
          <p className="mt-1 text-xs font-light text-cream/45">Canal principal por ahora.</p>
        )}
      </div>
    </div>
  );

  return href ? (
    <a href={href} className="block transition-transform hover:-translate-y-0.5">
      {inner}
    </a>
  ) : (
    inner
  );
};

export const Contact = () => {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Por favor completa todos los campos.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Gracias. Tu mensaje fue enviado correctamente.");
      setForm({ name: "", email: "", message: "" });
    } catch (err) {
      toast.error(
        "No pudimos enviar tu mensaje. Escríbenos directamente a josecguevara7@gmail.com."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {/* Hero */}
      <section
        className="grain relative overflow-hidden bg-navy-deep pt-32 pb-16 sm:pt-40 sm:pb-20"
        data-testid="contact-hero"
      >
        <div className="container-luxe relative z-10 max-w-3xl">
          <Reveal>
            <p className="overline mb-6 flex items-center gap-4">
              <span className="h-px w-10 bg-gold" /> Contacto
            </p>
          </Reveal>
          <Reveal delay={0.1}>
            <h1 className="font-serif text-4xl font-light leading-[1.05] tracking-tight text-cream sm:text-5xl lg:text-6xl">
              La conversación <span className="italic text-gold">puede continuar</span>.
            </h1>
          </Reveal>
          <Reveal delay={0.2}>
            <p className="mt-8 max-w-xl text-base font-light leading-relaxed text-cream/70 sm:text-lg">
              Si este recorrido despertó una pregunta, una reflexión o simplemente el deseo de seguir
              profundizando, será un gusto escucharte.
            </p>
          </Reveal>
        </div>
      </section>

      {/* Canales + Formulario */}
      <section
        className="relative overflow-hidden bg-navy-deep pb-24 sm:pb-32"
        data-testid="contact-section"
      >
        <div className="container-luxe relative z-10">
          <div className="grid grid-cols-1 gap-16 lg:grid-cols-12">
            {/* Canales */}
            <div className="lg:col-span-5">
              <Reveal>
                <p className="overline mb-6 flex items-center gap-4">
                  <span className="h-px w-10 bg-gold" /> Estamos aquí
                </p>
              </Reveal>
              <Reveal delay={0.1}>
                <h2 className="font-serif text-3xl font-light leading-tight tracking-tight text-cream sm:text-4xl">
                  Elige la forma que te resulte más cómoda.
                </h2>
              </Reveal>
              <div className="mt-10 flex flex-col gap-4">
                <Reveal delay={0.15}>
                  <Channel
                    icon={Mail}
                    label="Correo electrónico"
                    value="josecguevara7@gmail.com"
                    href="mailto:josecguevara7@gmail.com"
                    primary
                    testid="contact-channel-email"
                  />
                </Reveal>
                <Reveal delay={0.22}>
                  <Channel
                    icon={MessageCircle}
                    label="WhatsApp"
                    value="Próximamente"
                    testid="contact-channel-whatsapp"
                  />
                </Reveal>
                <Reveal delay={0.29}>
                  <Channel
                    icon={Instagram}
                    label="Instagram"
                    value="Próximamente"
                    testid="contact-channel-instagram"
                  />
                </Reveal>
              </div>
            </div>

            {/* Formulario */}
            <div className="lg:col-span-6 lg:col-start-7">
              <Reveal delay={0.15}>
                <form onSubmit={submit} className="flex flex-col gap-8" data-testid="contact-form">
                  <Field
                    label="Nombre"
                    placeholder="Tu nombre"
                    value={form.name}
                    onChange={set("name")}
                    data-testid="contact-name"
                  />
                  <Field
                    label="Correo electrónico"
                    type="email"
                    placeholder="tu@email.com"
                    value={form.email}
                    onChange={set("email")}
                    data-testid="contact-email"
                  />
                  <Field
                    label="Mensaje"
                    textarea
                    placeholder="Escríbenos lo que quieras compartir…"
                    value={form.message}
                    onChange={set("message")}
                    data-testid="contact-message"
                  />
                  <button
                    type="submit"
                    disabled={loading}
                    className="btn-gold mt-2 self-start disabled:opacity-60"
                    data-testid="contact-submit"
                  >
                    {loading ? <Loader2 className="animate-spin" size={16} /> : <ArrowRight size={16} />}
                    {loading ? "Enviando…" : "Enviar mensaje"}
                  </button>
                </form>
              </Reveal>
            </div>
          </div>

          {/* Qué puedes esperar */}
          <div className="mt-24 border-t border-gold/15 pt-16 sm:mt-32">
            <div className="max-w-2xl">
              <Reveal>
                <h2 className="font-serif text-3xl font-light leading-tight tracking-tight text-cream sm:text-4xl">
                  Responderemos de forma <span className="italic text-gold">personal</span>.
                </h2>
              </Reveal>
              <Reveal delay={0.1}>
                <p className="mt-6 text-base font-light leading-relaxed text-cream/70 sm:text-lg">
                  Cada mensaje será leído con atención. Nuestro propósito es responderte de manera
                  cercana, clara y humana.
                </p>
              </Reveal>
            </div>
          </div>

          {/* Cierre */}
          <div className="mt-24 sm:mt-32" data-testid="contact-closing">
            <Reveal>
              <div className="mx-auto max-w-2xl text-center">
                <p className="font-serif text-2xl font-light leading-relaxed text-cream sm:text-3xl">
                  Gracias por recorrer la Academia Desde el Primer Latido™.
                </p>
                <p className="mt-6 font-serif text-xl font-light italic leading-relaxed text-gold sm:text-2xl">
                  Todo gran cambio comienza con una conversación.
                </p>
                <p className="mt-6 text-base font-light leading-relaxed text-cream/70">
                  Si sientes que este camino también puede formar parte de tu historia, aquí estaremos
                  para escucharte.
                </p>
              </div>
            </Reveal>
          </div>
        </div>
      </section>
    </>
  );
};
